import type { MemberRole } from '../../domain/access'
import { CARAVAN_TRIP } from '../../config/trip'
import {
  CANONICAL_CHALLENGE_IDS,
  SECRET_MISSIONS,
  TRIP_DAY_PROGRAMS,
  getChallengeResponse,
  missionAssignmentRows,
  type ChallengeContentRole,
} from '../../config/tripChallenges'
import {
  challengeDayPhase,
  validateChallengeInput,
  validateDistinctAssignments,
  type ChallengeAssignmentInput,
  type ChallengeInput,
} from '../../domain/challenge'
import { displayedChallengePhase } from '../../domain/challengePreview'
import { isTripProductDay } from '../../domain/tripTime'
import type { LocalEntity } from '../../domain/sync'
import type { CaravanDb, LocalBlob, LocalChallengeRecord } from './db'
import { assertTripWritable, LocalRepository } from './repository'
import { validateVideoMetadata, type PreparedChallengeVideo } from '../../services/videoPipeline'

export type ChallengeView = {
  id: string
  kind: LocalChallengeRecord['kind']
  productDay: string | null
  title: string | null
  detail: string | null
  category: LocalChallengeRecord['category']
  safetyNote: string
  phase: 'locked' | 'active' | 'group_revealed'
  assignmentMemberId: string | null
  manuallyRevealed: boolean
  contentRole: ChallengeContentRole | null
}
export type ChallengeCompletionInput = {
  tripId: string
  challengeId: string
  memberId: string
  productDay: string
  text: string
  answers?: Record<string, string>
  photoId: string | null
  video?: PreparedChallengeVideo | null
  createdAtDevice: string
}
export type ChallengeCompletion = ChallengeCompletionInput & {
  id: string
  videoId: string | null
  videoDurationSeconds: number | null
  videoConfirmedRemote: boolean
  lastMutationId: string
  syncState: LocalEntity['syncState']
}

const CANONICAL_CREATED_AT = '2026-08-22T00:00:00.000Z'
const FINAL_MISSION_REVEAL_DAY = '2026-09-18'
const CANONICAL_CREATOR_ID = CARAVAN_TRIP.participants[0]?.id ?? '11111111-1111-4111-8111-111111111111'

function payloadString(entity: LocalEntity, key: string): string {
  return typeof entity.payload[key] === 'string' ? entity.payload[key] : ''
}

function completionFromEntity(entity: LocalEntity, videoConfirmedRemote = false): ChallengeCompletion {
  return {
    id: entity.id,
    tripId: entity.tripId,
    challengeId: payloadString(entity, 'challengeId'),
    memberId: payloadString(entity, 'memberId'),
    productDay: payloadString(entity, 'productDay'),
    text: payloadString(entity, 'text'),
    answers: readAnswers(entity.payload.answers),
    photoId: typeof entity.payload.photoId === 'string' ? entity.payload.photoId : null,
    videoId: typeof entity.payload.videoId === 'string' ? entity.payload.videoId : null,
    videoDurationSeconds: typeof entity.payload.videoDurationSeconds === 'number' ? entity.payload.videoDurationSeconds : null,
    videoConfirmedRemote,
    video: null,
    createdAtDevice: entity.createdAtDevice,
    lastMutationId: entity.lastMutationId,
    syncState: entity.syncState,
  }
}

function readAnswers(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'))
}

function canonicalRecords(): LocalChallengeRecord[] {
  const daily = TRIP_DAY_PROGRAMS.flatMap((program): LocalChallengeRecord[] => [
    {
      id: program.media.id,
      tripId: CARAVAN_TRIP.id,
      kind: 'daily',
      productDay: program.productDay,
      title: program.media.title,
      detail: program.media.detail,
      category: program.media.category,
      safetyNote: '',
      createdBy: CANONICAL_CREATOR_ID,
      createdAt: CANONICAL_CREATED_AT,
      manuallyRevealedAt: null,
      contentRole: 'daily_media',
    },
    {
      id: program.challenge.id,
      tripId: CARAVAN_TRIP.id,
      kind: 'daily',
      productDay: program.productDay,
      title: program.challenge.title,
      detail: program.challenge.detail,
      category: program.challenge.category,
      safetyNote: program.challenge.safetyNote,
      createdBy: CANONICAL_CREATOR_ID,
      createdAt: CANONICAL_CREATED_AT,
      manuallyRevealedAt: null,
      contentRole: 'daily_challenge',
    },
  ])
  const missions = SECRET_MISSIONS.map((mission): LocalChallengeRecord => ({
    id: mission.id,
    tripId: CARAVAN_TRIP.id,
    kind: 'trip_secret',
    productDay: null,
    title: mission.title,
    detail: mission.detail,
    category: mission.category,
    safetyNote: mission.safetyNote,
    createdBy: CANONICAL_CREATOR_ID,
    createdAt: CANONICAL_CREATED_AT,
    manuallyRevealedAt: null,
    contentRole: 'secret_mission',
  }))
  return [...daily, ...missions]
}

function validateAnswers(challengeId: string, answers: Record<string, string>, hasPhoto: boolean, video: PreparedChallengeVideo | null): void {
  const response = getChallengeResponse(challengeId)
  if (!response) return
  for (const field of response.fields) {
    const value = answers[field.key]?.trim() ?? ''
    if (field.required && !value) throw new Error(`Renseigne le champ « ${field.label} ».`)
    if (field.maxLength !== undefined && value.length > field.maxLength) throw new Error(`Le champ « ${field.label} » est trop long.`)
    if (field.kind === 'url' && value) {
      try {
        const url = new URL(value)
        if (!['http:', 'https:'].includes(url.protocol)) throw new Error()
      } catch {
        throw new Error(`Le lien « ${field.label} » doit commencer par http:// ou https://.`)
      }
    }
  }
  if (hasPhoto && !response.allowPhoto) throw new Error('Ce défi ne prévoit pas de photo jointe.')
  if (response.video?.required && !video) throw new Error('Ajoute la vidéo demandée pour conserver cette réalisation.')
  if (video && !response.video) throw new Error('Ce défi ne prévoit pas de vidéo.')
  if (video && response.video) {
    validateVideoMetadata({ type: video.file.type, size: video.file.size, durationSeconds: video.durationSeconds }, response.video)
  }
}

function answerSummary(answers: Record<string, string>): string {
  return Object.values(answers).map((value) => value.trim()).filter(Boolean).join(' · ')
}

export class ChallengeRepository {
  readonly #db: CaravanDb
  readonly #local: LocalRepository

  constructor(db: CaravanDb) {
    this.#db = db
    this.#local = new LocalRepository(db)
  }

  async ensureCanonicalContent(): Promise<void> {
    const records = canonicalRecords()
    const assignments = missionAssignmentRows().map((assignment) => ({
      key: `${assignment.challengeId}:${assignment.memberId}`,
      challengeId: assignment.challengeId,
      tripId: CARAVAN_TRIP.id,
      memberId: assignment.memberId,
      assignedAt: CANONICAL_CREATED_AT,
    }))
    await this.#db.transaction('rw', this.#db.challenges, this.#db.challengeAssignments, async () => {
      const existingRecords = await this.#db.challenges.bulkGet(records.map((record) => record.id))
      const refreshedRecords = records.map((record, index) => ({
        ...record,
        manuallyRevealedAt: existingRecords[index]?.manuallyRevealedAt ?? null,
      }))
      await this.#db.challenges.bulkPut(refreshedRecords)

      const existingKeys = new Set((await this.#db.challengeAssignments.bulkGet(assignments.map((assignment) => assignment.key)))
        .filter((assignment) => Boolean(assignment))
        .map((assignment) => assignment?.key))
      const missingAssignments = assignments.filter((assignment) => !existingKeys.has(assignment.key))
      if (missingAssignments.length > 0) await this.#db.challengeAssignments.bulkAdd(missingAssignments)
    })
  }

  async prepareChallenge(input: ChallengeInput, role: MemberRole, creatorId: string): Promise<LocalChallengeRecord> {
    const clean = validateChallengeInput(input, role)
    await assertTripWritable(this.#db, clean.tripId)
    const record: LocalChallengeRecord = { ...clean, id: crypto.randomUUID(), createdBy: creatorId, createdAt: new Date().toISOString(), manuallyRevealedAt: null }
    await this.#db.transaction('rw', this.#db.challenges, this.#db.entities, this.#db.outbox, this.#db.tripArchives, async () => {
      await this.#db.challenges.add(record)
      await this.#local.mutate({
        mutationId: crypto.randomUUID(), entityType: 'challenge', entityId: record.id,
        tripId: record.tripId, operation: 'create', baseVersion: 0,
        payload: {
          kind: record.kind, productDay: record.productDay, title: record.title,
          detail: record.detail, category: record.category, safetyNote: record.safetyNote,
          creatorMemberId: record.createdBy,
        },
        createdAtDevice: record.createdAt,
      })
    })
    return record
  }

  async assignDistinct(assignments: ChallengeAssignmentInput[], expectedMembers: string[], role: MemberRole): Promise<void> {
    if (role !== 'admin') throw new Error('Seul l’administrateur peut attribuer les défis.')
    const clean = validateDistinctAssignments(assignments, expectedMembers)
    const assignedAt = new Date().toISOString()
    const rows = await Promise.all(clean.map(async (item) => {
      const challenge = await this.#db.challenges.get(item.challengeId)
      if (challenge?.kind !== 'trip_secret') throw new Error('Défi secret introuvable.')
      return {
        key: `${item.challengeId}:${item.memberId}`,
        challengeId: item.challengeId,
        tripId: challenge.tripId,
        memberId: item.memberId,
        assignedAt,
      }
    }))
    const tripIds = new Set(rows.map((row) => row.tripId))
    if (tripIds.size !== 1) throw new Error('Les défis attribués doivent appartenir au même voyage.')
    const tripId = rows[0]?.tripId
    if (!tripId) throw new Error('Aucune attribution à enregistrer.')
    await assertTripWritable(this.#db, tripId)
    await this.#db.transaction('rw', this.#db.challengeAssignments, this.#db.entities, this.#db.outbox, this.#db.tripArchives, async () => {
      await this.#db.challengeAssignments.bulkPut(rows)
      await this.#local.mutate({
        mutationId: crypto.randomUUID(), entityType: 'challenge_assignment', entityId: tripId,
        tripId, operation: 'upsert', baseVersion: 0,
        payload: { assignments: clean }, createdAtDevice: assignedAt,
      })
    })
  }

  async listChallengesFor(memberId: string, isAdmin: boolean, now = new Date()): Promise<ChallengeView[]> {
    await this.ensureCanonicalContent()
    const challenges = await this.#db.challenges.toArray()
    const assignments = await this.#db.challengeAssignments.toArray()
    return challenges.flatMap((challenge): ChallengeView[] => {
      const assignment = assignments.find((item) => item.challengeId === challenge.id)
      const canonicalMission = challenge.contentRole === 'secret_mission' && CANONICAL_CHALLENGE_IDS.has(challenge.id)
      const missionsRevealed = challengeDayPhase(FINAL_MISSION_REVEAL_DAY, now) === 'group_revealed'
      if (canonicalMission && !missionsRevealed && assignment?.memberId !== memberId) return []
      if (challenge.kind === 'trip_secret' && !canonicalMission && !isAdmin && assignment?.memberId !== memberId) return []
      const scheduledPhase = challenge.kind === 'daily' && challenge.productDay
        ? displayedChallengePhase(challenge.productDay, now)
        : canonicalMission && missionsRevealed ? 'group_revealed' : 'active'
      const phase = challenge.manuallyRevealedAt && scheduledPhase === 'active'
        ? 'group_revealed'
        : scheduledPhase
      const protectedDaily = challenge.kind === 'daily' && phase === 'locked'
      return [{
        id: challenge.id,
        kind: challenge.kind,
        productDay: challenge.productDay,
        title: protectedDaily ? null : challenge.title,
        detail: protectedDaily ? null : challenge.detail,
        category: challenge.category,
        safetyNote: challenge.safetyNote,
        phase,
        assignmentMemberId: assignment?.memberId ?? null,
        manuallyRevealed: challenge.manuallyRevealedAt !== null,
        contentRole: challenge.contentRole ?? null,
      }]
    })
  }

  async complete(input: ChallengeCompletionInput): Promise<ChallengeCompletion> {
    await this.ensureCanonicalContent()
    const challenge = await this.#db.challenges.get(input.challengeId)
    if (!challenge) throw new Error('Défi introuvable.')
    if (challenge.kind === 'daily' && (
      challenge.productDay !== input.productDay || !isTripProductDay(input.productDay)
    )) {
      throw new Error('La réalisation quotidienne doit rester dans les dates du voyage.')
    }
    if (challenge.kind === 'daily' && challengeDayPhase(input.productDay, new Date(input.createdAtDevice)) !== 'active') {
      throw new Error('Ce défi n’est pas ouvert pour le moment.')
    }
    const answers = Object.fromEntries(Object.entries(input.answers ?? {}).map(([key, value]) => [key, value.trim()]))
    const text = input.text.trim() || answerSummary(answers)
    const video = input.video ?? null
    validateAnswers(input.challengeId, answers, Boolean(input.photoId), video)
    if (!getChallengeResponse(input.challengeId) && !text && !input.photoId && !video) throw new Error('Ajoute un récit, une photo ou une vidéo à ta réalisation.')
    if (CANONICAL_CHALLENGE_IDS.has(input.challengeId)) {
      const existing = await this.#db.entities.where('entityType').equals('challenge_completion')
        .filter((entity) => entity.deletedAt === null && entity.payload.challengeId === input.challengeId && entity.payload.memberId === input.memberId)
        .count()
      if (existing > 0) throw new Error('Ta réalisation est déjà conservée pour ce défi.')
    }
    const videoId = video ? crypto.randomUUID() : null
    if (video && videoId) {
      const rows: LocalBlob[] = [{
        key: `${videoId}:original`, entityId: videoId, kind: 'original', bytes: await video.file.arrayBuffer(),
        mimeType: video.file.type, confirmedRemote: false, createdAt: input.createdAtDevice,
      }]
      if (video.thumbnail) rows.push({
        key: `${videoId}:thumbnail`, entityId: videoId, kind: 'thumbnail', bytes: await video.thumbnail.arrayBuffer(),
        mimeType: video.thumbnail.type, confirmedRemote: false, createdAt: input.createdAtDevice,
      })
      await this.#db.blobs.bulkPut(rows)
    }
    const id = crypto.randomUUID()
    try {
      const entity = await this.#local.mutate({
        mutationId: crypto.randomUUID(),
        entityType: 'challenge_completion',
        entityId: id,
        tripId: input.tripId,
        operation: 'create',
        baseVersion: 0,
        payload: {
          challengeId: input.challengeId,
          memberId: input.memberId,
          productDay: input.productDay,
          text,
          answers,
          photoId: input.photoId,
          videoId,
          videoDurationSeconds: video?.durationSeconds ?? null,
        },
        createdAtDevice: input.createdAtDevice,
      })
      return completionFromEntity(entity)
    } catch (error) {
      if (videoId) await this.#db.blobs.where('entityId').equals(videoId).delete()
      throw error
    }
  }

  async getVideo(videoId: string): Promise<Blob | null> {
    const stored = await this.#db.blobs.get(`${videoId}:original`)
    return stored ? new Blob([stored.bytes], { type: stored.mimeType }) : null
  }

  async getVideoThumbnail(videoId: string): Promise<Blob | null> {
    const stored = await this.#db.blobs.get(`${videoId}:thumbnail`)
    return stored ? new Blob([stored.bytes], { type: stored.mimeType }) : null
  }

  async listCompletionsFor(memberId: string, now = new Date()): Promise<ChallengeCompletion[]> {
    const rows = await this.#db.entities.where('entityType').equals('challenge_completion')
      .filter((entity) => entity.deletedAt === null)
      .toArray()
    const completions = await Promise.all(rows.map(async (entity) => {
      const videoId = typeof entity.payload.videoId === 'string' ? entity.payload.videoId : null
      const videoRows = videoId ? await this.#db.blobs.where('entityId').equals(videoId).toArray() : []
      return completionFromEntity(entity, videoRows.length > 0 && videoRows.every((row) => row.confirmedRemote))
    }))
    return completions
      .filter((completion) => completion.memberId === memberId || challengeDayPhase(completion.productDay, now) === 'group_revealed')
      .sort((left, right) => left.createdAtDevice.localeCompare(right.createdAtDevice))
  }

  async countCompletionsFor(challengeId: string): Promise<number> {
    return this.#db.entities.where('entityType').equals('challenge_completion')
      .filter((entity) => entity.deletedAt === null && entity.payload.challengeId === challengeId)
      .count()
  }

  async undoPendingCompletion(completionId: string, memberId: string): Promise<boolean> {
    const entity = await this.#db.entities.get(`challenge_completion:${completionId}`)
    if (entity?.payload.memberId !== memberId) return false
    const mutation = await this.#db.outbox.get(entity.lastMutationId)
    if (!mutation || mutation.state === 'synced' || mutation.state === 'syncing') return false
    const videoId = typeof entity.payload.videoId === 'string' ? entity.payload.videoId : null
    await this.#db.transaction('rw', this.#db.entities, this.#db.outbox, this.#db.blobs, async () => {
      await this.#db.entities.delete(entity.key)
      await this.#db.outbox.delete(entity.lastMutationId)
      if (videoId) await this.#db.blobs.where('entityId').equals(videoId).delete()
    })
    return true
  }

  async revealNow(challengeId: string, role: MemberRole): Promise<void> {
    if (role !== 'admin') throw new Error('Seul l’administrateur peut révéler un défi.')
    const challenge = await this.#db.challenges.get(challengeId)
    if (!challenge) throw new Error('Défi introuvable.')
    await assertTripWritable(this.#db, challenge.tripId)
    const revealedAt = new Date().toISOString()
    await this.#db.transaction('rw', this.#db.challenges, this.#db.entities, this.#db.outbox, this.#db.tripArchives, async () => {
      await this.#db.challenges.update(challengeId, { manuallyRevealedAt: revealedAt })
      const existing = await this.#db.entities.get(`reveal:${challengeId}`)
      await this.#local.mutate({
        mutationId: crypto.randomUUID(), entityType: 'reveal', entityId: challengeId,
        tripId: challenge.tripId, operation: 'update', baseVersion: existing?.version ?? 0,
        payload: { kind: 'challenge', state: 'revealed', revealedAt }, createdAtDevice: revealedAt,
      })
    })
  }
}
